import { Binary } from "./binary.js";
import { breakModels } from "./model-physics.js";
import { modelParticles } from "./model-particles.js";
import * as T from "three";

// Disk layouts from Source SDK public/studio.h (not native pointer layouts).
export function parseSkeleton(bytes) {
    const mdl = new Binary(bytes);
    if (mdl.text(0, 4) !== "IDST" || ![44, 45, 46, 47, 48, 49].includes(mdl.i32(4))) throw new Error("Unsupported studio header");
    function table(countOffset, stride, limit) {
        const count = mdl.i32(countOffset), start = mdl.i32(countOffset + 4);
        if (count < 0 || count > limit) throw new Error("Studio table exceeds budget");
        mdl.check(start, count * stride); return Array.from({ length: count }, (_, i) => start + i * stride);
    }
    const bones = table(156, 216, 256).map(offset => ({
        name: mdl.string(offset + mdl.i32(offset)), parent: mdl.i32(offset + 4),
        position: mdl.vec(offset + 32), quaternion: Array.from({ length: 4 }, (_, i) => mdl.f32(offset + 44 + i * 4)),
        rotation: mdl.vec(offset + 60), positionScale: mdl.vec(offset + 72), rotationScale: mdl.vec(offset + 84),
        inverseBind: Array.from({ length: 12 }, (_, i) => mdl.f32(offset + 96 + i * 4)),
        flags: mdl.u32(offset + 160),
    }));
    for (let i = 0; i < bones.length; i++) {
        const visited = new Set([i]); let parent = bones[i].parent;
        while (parent !== -1) {
            if (parent < 0 || parent >= bones.length || visited.has(parent)) throw new Error("Invalid studio bone hierarchy");
            visited.add(parent); parent = bones[parent].parent;
        }
        const magnitude = Math.hypot(...bones[i].quaternion);
        if (magnitude < 0.001) throw new Error("Invalid studio bone quaternion");
        bones[i].quaternion = bones[i].quaternion.map(value => value / magnitude);
    }
    const attachments = table(240, 92, 256).map(offset => {
        const bone = mdl.i32(offset + 8);
        if (bone < 0 || bone >= bones.length) throw new Error("Invalid attachment bone");
        return { name: mdl.string(offset + mdl.i32(offset)), bone,
            matrix: Array.from({ length: 12 }, (_, i) => mdl.f32(offset + 12 + i * 4)) };
    });
    const poses = table(300, 20, 24).map(offset => ({ name: mdl.string(offset + mdl.i32(offset)),
        flags: mdl.u32(offset + 4), start: mdl.f32(offset + 8), end: mdl.f32(offset + 12), loop: mdl.f32(offset + 16) }));
    const kvOffset = mdl.i32(312), kvSize = mdl.i32(316);
    if (kvSize < 0 || kvSize > 256 * 1024) throw new Error("Studio KeyValues exceeds budget");
    if (kvSize) mdl.check(kvOffset, kvSize);
    const text = kvSize ? mdl.text(kvOffset, kvSize) : '';
    const breaks = kvSize ? breakModels(text) : [];

    return { bones, attachments, poses, breakModels: breaks, particleEffects: modelParticles(text) };
}
export function studioMatrix(values) {
    return new T.Matrix4().set(...values.slice(0, 4), ...values.slice(4, 8), ...values.slice(8, 12), 0, 0, 0, 1);
}
/** Bone objects and GPU palette belong to one entity, even when source data is shared. */
export function createSkeleton(data, root) {
    const bones = data.bones.map(source => {
        const bone = new T.Bone(); bone.name = source.name;
        bone.position.fromArray(source.position); bone.quaternion.fromArray(source.quaternion); return bone;
    });
    bones.forEach((bone, i) => (data.bones[i].parent < 0 ? root : bones[data.bones[i].parent]).add(bone));
    const skeleton = new T.Skeleton(bones, data.bones.map(bone => studioMatrix(bone.inverseBind)));
    const attachments = data.attachments.map(source => {
        const object = new T.Object3D(); object.name = source.name; object.matrixAutoUpdate = false;
        object.matrix.copy(studioMatrix(source.matrix)); bones[source.bone].add(object); return object;
    });
    return { skeleton, bones, attachments, data };
}
